import Form from './form';
import ITA from './ita';
import Record from './record';
import ChoiceList from './choice-list';
import ClassificationSet from './classification-set';
import Membership from './membership';
import Project from './project';
import Layer from './layer';
import Group from './group';
import Role from './role';
import Photo from './photo';
import Attachment from './attachment';
import Video from './video';
import Audio from './audio';
import Signature from './signature';
import Changeset from './changeset';
import Query from './query';
import Export from './export';
import View from './view';
import Permission from './permission';
import Share from './share';
import ReportTemplate from './report-template';
import Workflow from './workflow';
import WorkflowExecution from './workflow_execution';
import ExternalContributionsApiClient from './external-contributions-api-client';
import Sketch from './sketch';
import { Config } from '../models/config';
import { ResourceClient } from './resource';
import { ServiceRegistry, ResourceRegistry } from './resources-types';

const BASE = '/api/v2';
const UA = 'Fulcrum Web';

export default class Client implements ResourceClient {
  config: Config;
  token: string;
  base: string;
  userAgent: string;
  request: Function;

  private readonly resourceInstances: Map<keyof ResourceRegistry, any> = new Map();
  private readonly serviceInstances: Map<keyof ServiceRegistry, any> = new Map();

  constructor(options) {
    this.request = options.request;
    this.token = options.token;
    this.base = options.base || BASE;
    this.userAgent = options.userAgent || UA;
    this.config = options.config;
  }

  urlFromPath(path, base?) {
    return `${(base || this.base)}/${path}`;
  }

  call(requestOptions) {
    const options = requestOptions || {};

    if (options.path) {
      options.url = this.urlFromPath(options.path, options.base);
    }

    // TODO(zhm) using the query string token param eliminates a lot of OPTIONS
    // cors preflight requests
    options.headers = {
      Accept: 'application/json',
    };

    if (!options.qs) {
      options.qs = {};
    }

    options.withCredentials = true;

    if (this.token) {
      options.headers['X-ApiToken'] = this.token;
    }

    return new Promise((resolve, reject) => {
      this.request(options, (err, res, body) => {
        if (err) {
          console.error('Request Error', body);

          err.res = res;
          err.body = body;

          return reject(err);
        }

        return resolve(body);
      });
    });
  }

  get(options) {
    return this.call({ method: 'GET', ...options });
  }

  post(options) {
    return this.call({ method: 'POST', ...options });
  }

  put(options) {
    return this.call({ method: 'PUT', ...options });
  }

  delete(options) {
    return this.call({ method: 'DELETE', ...options });
  }

  url(path, base) {
    return this.urlFromPath(path, base);
  }

  private getResource<K extends keyof ResourceRegistry>(
    key: K,
    ResourceClass: new (clientProvider: () => ResourceClient) => ResourceRegistry[K],
  ): ResourceRegistry[K] {
    if (!this.resourceInstances.has(key)) {
      this.resourceInstances.set(key, new ResourceClass(() => this));
    }
    return this.resourceInstances.get(key);
  }

  private getServiceResource<K extends keyof ServiceRegistry>(
    key: K,
    factory: () => ServiceRegistry[K],
  ): ServiceRegistry[K] {
    if (!this.serviceInstances.has(key)) {
      this.serviceInstances.set(key, factory());
    }
    return this.serviceInstances.get(key);
  }

  get choiceLists() {
    return this.getResource('choiceLists', ChoiceList);
  }

  get classificationSets() {
    return this.getResource('classificationSet', ClassificationSet);
  }

  get forms() {
    return this.getResource('forms', Form);
  }

  get ita() {
    return this.getResource('ita', ITA);
  }

  get records() {
    return this.getResource('records', Record);
  }

  get memberships() {
    return this.getResource('memberships', Membership);
  }

  get projects() {
    return this.getResource('projects', Project);
  }

  get layers() {
    return this.getResource('layers', Layer);
  }

  get groups() {
    return this.getResource('groups', Group);
  }

  get roles() {
    return this.getResource('roles', Role);
  }

  get photos() {
    return this.getResource('photos', Photo);
  }

  get attachments() {
    return this.getResource('attachments', Attachment);
  }

  get videos() {
    return this.getResource('videos', Video);
  }

  get audio() {
    return this.getResource('audio', Audio);
  }

  get signatures() {
    return this.getResource('signatures', Signature);
  }

  get changesets() {
    return this.getResource('changesets', Changeset);
  }

  get exports() {
    return this.getResource('exports', Export);
  }

  get views() {
    return this.getResource('views', View);
  }

  get shares() {
    return this.getResource('shares', Share);
  }

  get permissions() {
    return this.getResource('permissions', Permission);
  }

  get reportTemplates() {
    return this.getResource('reportTemplates', ReportTemplate);
  }

  get query() {
    return this.getResource('query', Query);
  }

  get workflows() {
    return this.getResource('workflow', Workflow);
  }

  get workflowExecutions() {
    return this.getResource('workflowExecutions', WorkflowExecution);
  }

  get externalContributions(): ExternalContributionsApiClient {
    return this.getServiceResource(
      'externalContributions',
      () => new ExternalContributionsApiClient(this.config.external_contributions_url, this.token),
    );
  }

  get sketches() {
    return this.getResource('sketches', Sketch);
  }
}
